import React from "react";
import ProvinceSelect from "../ProvinceSelect";
import { useAuth } from "../../contexts/AuthContext";
import { BILLING, BILLING_TYPES, type BillingType, type RatesDraft } from "../../lib/load-rates";

/**
 * The billing and rate boxes for a trucking load, shared by New Load and the
 * BOL's Rates panel. Every box is optional: on a new load a blank box is filled
 * from the rate card, and a typed "0" is a real $0 (see lib/load-rates.ts).
 *
 * The province is where the WORK is done, which is what the invoice is taxed
 * on (api#954). It is not the customer's billing province.
 */
const input =
  "h-11 w-full rounded-fi-control border border-fieldiq-border bg-fieldiq-dark px-3 text-sm text-fieldiq-text placeholder:text-fieldiq-muted focus:border-fi-amber focus:outline-none";
const label = "mb-1.5 block text-[10px] font-bold uppercase tracking-wider text-fieldiq-muted";

export default function LoadRatesFields({
  draft,
  onChange,
  idPrefix,
  mode,
}: {
  draft: RatesDraft;
  onChange: (next: RatesDraft) => void;
  idPrefix: string;
  /** 'create': a blank box is filled from the card. 'edit': a blank box clears the value. */
  mode: "create" | "edit";
}) {
  const { company } = useAuth();
  const country = (company as { country?: string } | null)?.country === "US" ? "US" : "CA";
  const b = BILLING[draft.billingType];
  const set = <K extends keyof RatesDraft>(key: K, value: RatesDraft[K]) => onChange({ ...draft, [key]: value });
  const id = (s: string) => `${idPrefix}-${s}`;
  const fromCard = mode === "create" ? "From the rate card" : undefined;

  return (
    <div className="space-y-4" data-testid="load-rates-fields">
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor={id("billing")} className={label}>Billing type</label>
          <select
            id={id("billing")}
            className={input}
            value={draft.billingType}
            onChange={(e) => set("billingType", e.target.value as BillingType)}
          >
            {BILLING_TYPES.map((t) => <option key={t} value={t}>{BILLING[t].label}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor={id("province")} className={label}>{country === "US" ? "State of the work" : "Province of the work"}</label>
          <ProvinceSelect id={id("province")} value={draft.provinceState} onChange={(v) => set("provinceState", v)} country={country} className={input} />
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor={id("haul")} className={label}>{b.rateLabel}</label>
          <input id={id("haul")} inputMode="decimal" className={input} value={draft.haulRate} onChange={(e) => set("haulRate", e.target.value)} placeholder={fromCard} />
        </div>
        {b.qtyLabel && (
          <div>
            <label htmlFor={id("qty")} className={label}>{b.qtyLabel}</label>
            <input id={id("qty")} inputMode="decimal" className={input} value={draft.quantity} onChange={(e) => set("quantity", e.target.value)} />
          </div>
        )}
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor={id("wait-rate")} className={label}>Wait rate (per hour)</label>
          <input id={id("wait-rate")} inputMode="decimal" className={input} value={draft.waitHourlyRate} onChange={(e) => set("waitHourlyRate", e.target.value)} placeholder={fromCard} />
        </div>
        <div>
          <label htmlFor={id("free-wait")} className={label}>Free wait minutes</label>
          <input id={id("free-wait")} inputMode="numeric" className={input} value={draft.freeWaitMinutes} onChange={(e) => set("freeWaitMinutes", e.target.value)} placeholder={fromCard} />
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor={id("fuel-basis")} className={label}>Fuel surcharge type</label>
          <select
            id={id("fuel-basis")}
            className={input}
            value={draft.fuelBasis}
            onChange={(e) => set("fuelBasis", e.target.value as RatesDraft["fuelBasis"])}
          >
            <option value="">{mode === "create" ? "From the rate card" : "None"}</option>
            <option value="fixed">Fixed amount</option>
            <option value="percent">Percent of haul</option>
          </select>
        </div>
        <div>
          <label htmlFor={id("fuel-value")} className={label}>{draft.fuelBasis === "percent" ? "Fuel surcharge (%)" : "Fuel surcharge"}</label>
          <input id={id("fuel-value")} inputMode="decimal" className={input} value={draft.fuelValue} onChange={(e) => set("fuelValue", e.target.value)} disabled={!draft.fuelBasis} />
        </div>
      </div>
    </div>
  );
}
